import { z } from 'zod';

import { zFirestampOptional } from '@elara/firebolt/zod-types';

import { WidgetImageSchema } from './image-schemas.js';
import { LabeledChargeSchema } from './shared-schemas.js';

/**
 * Occupancy configuration for a single room in a hotel search.
 * One occupancy = one room. Multi-room bookings with different configs
 * require multiple searches (one per occupancy).
 *
 * This schema is also re-exported as `HotelRoomRequestSchema` from the
 * Sabre vendor module for backwards compatibility.
 */
export const OccupancySchema = z
  .object({
    adults: z.number().int().min(1).max(8).describe('Adults in the room (required, 1-8)'),
    children: z.number().int().min(0).max(8).optional().describe('Children under 18 in the room'),
    childrenAges: z
      .array(z.number().int().min(0).max(17))
      .optional()
      .describe('Ages of each child. If provided, length must match `children`.'),
  })
  .refine((data) => !data.childrenAges || data.childrenAges.length === (data.children ?? 0), {
    message: '`childrenAges` length must match `children`',
    path: ['childrenAges'],
  });

export type Occupancy = z.infer<typeof OccupancySchema>;

/**
 * Stable string key for an Occupancy, used for grouping rates and dedup checks.
 * `childrenAges` is sorted before encoding so the key is order-invariant.
 */
export function occupancyKey(occ: Occupancy): string {
  const adults = occ.adults;
  const children = occ.children ?? 0;
  const ages = occ.childrenAges ? occ.childrenAges.toSorted((a, b) => a - b).join('-') : '';
  return ages !== '' ? `a${adults}-c${children}-${ages}` : `a${adults}-c${children}`;
}

/**
 * Bed types
 */
export const BedTypesSchema = z.object({
  // Optional since the Travelport room source: Travelport describes beds by name ("King
  // Bed(s)"), not by Sabre code. Every client consumer already reads
  // `sabreBedTypeCode ?? code`, so widening this is inert for rendering.
  sabreBedTypeCode: z.number().optional(),
  description: z.string(),
  count: z.number(),

  /** @deprecated */
  code: z.number().optional(),
});

export type BedType = z.infer<typeof BedTypesSchema>;

/**
 * Room amenities
 */
export const RoomAmenitiesSchema = z.object({
  code: z.number(),
  description: z.string(),
  notes: z.string().optional(),
  complimentary: z.boolean().optional(),
  accessible: z.boolean().optional(),
});

export type RoomAmenity = z.infer<typeof RoomAmenitiesSchema>;

/**
 * Room image
 */
export const RoomImagesSchema = z.object({ url: z.string().url(), caption: z.string(), alt: z.string() });

export type RoomImage = z.infer<typeof RoomImagesSchema>;

export const DailyRateSchema = z.object({
  startDate: z.string(),
  endDate: z.string(),
  amountBeforeTax: z.number(),
  currency: z.string(),
});

export type DailyRate = z.infer<typeof DailyRateSchema>;

/**
 * Room rate pricing information
 */
export const RoomRatePricingSchema = z.object({
  currency: z.string(),
  totalPrice: z.number(),
  totalTax: z.number().optional(),
  totalFees: z.number().optional(),
  // Itemized, labeled tax/fee lines from Sabre (`Taxes.TaxGroups` / `Fees.FeeGroups`). Captured
  // alongside the aggregate totals so mandatory fees (e.g. "Resort fee") can be disclosed
  // line-by-line at the point of display (ELARA-829). Absent on legacy rates / when Sabre sends none.
  taxBreakdown: z.array(LabeledChargeSchema).optional(),
  feeBreakdown: z.array(LabeledChargeSchema).optional(),
  feesIncluded: z.boolean().optional(),
  taxesIncluded: z.boolean().optional(),
  // Whether local fees / incidentals are already folded into `totalPrice` (Sabre `LocalFeesInclusive` /
  // `IncidentalsInclusive`). With `feesIncluded` these decide "in the total" vs "due at the property".
  localFeesIncluded: z.boolean().optional(),
  incidentalsIncluded: z.boolean().optional(),
  avgNightlyRate: z.number(),
  avgNightlyRateBeforeTax: z.number().optional(),
  dailyRates: z.array(DailyRateSchema).optional(),
});

export type RoomRatePricing = z.infer<typeof RoomRatePricingSchema>;

export const AdditionalDetailSchema = z.object({
  sabreDetailCode: z.number().optional(),
  description: z.string().optional(),
  amount: z.number().optional(),
  currency: z.string().optional(),
  text: z.array(z.string()).optional(),
});

export type AdditionalDetail = z.infer<typeof AdditionalDetailSchema>;

export const DeadlineSchema = z.object({
  absoluteDeadline: z.string().optional(),
  offsetTimeUnit: z.string().optional(),
  offsetUnitMultiplier: z.number().optional(),
  offsetDropTime: z
    .enum(['BeforeArrival', 'AfterBooking', 'AfterConfirmation', 'AfterArrival', 'AfterDeparture'])
    .optional(),
});

export type Deadline = z.infer<typeof DeadlineSchema>;

export const AmountPercentSchema = z.object({
  taxInclusive: z.boolean().optional(),
  feesInclusive: z.boolean().optional(),
  numberOfNights: z.number().optional(),
  basisType: z.string().optional(),
  percent: z.number().multipleOf(0.01).optional(),
  amount: z.number().optional(),
  currency: z.string().optional(),
  /**
   * The money figure is the supplier's own estimate for this stay, converted from a rule such as
   * "one night" or "100%", not a stated fee. Travelport's search marks nearly every amount this way;
   * a client must see it as "about", never as the fee.
   */
  estimated: z.boolean().optional(),
});

export type AmountPercent = z.infer<typeof AmountPercentSchema>;

export const CancellationPolicySchema = z.object({
  refundable: z.boolean(),
  deadline: DeadlineSchema.optional(),
  amountPercent: AmountPercentSchema.optional(),
  penaltyDescription: z.array(z.string()).optional(),
  /**
   * Which supplier call stated this tier. Travelport's search (`search`) gives one tier with an
   * estimated amount and, measured across five chains, the wrong deadline or refundability on
   * Marriott brands and IHG prepaid rates; its Rules call (`rules`) gives every tier as the hotel
   * loaded it. A `rules` policy is never overwritten by a `search` one, and a card makes no
   * cancellation claim from a `search` policy until Rules has been read. Absent on Sabre.
   */
  source: z.enum(['search', 'rules']).optional(),
});

export type CancellationPolicy = z.infer<typeof CancellationPolicySchema>;

export const PaymentCardSchema = z.object({ sabreCardCode: z.string().optional(), name: z.string().optional() });

export type PaymentCard = z.infer<typeof PaymentCardSchema>;

export const GuaranteeSchema = z.object({
  sabreGuaranteeTypeCode: z.number().optional(),
  description: z.string().optional(),
  paymentCards: z.array(PaymentCardSchema).optional(),
});

export type Guarantee = z.infer<typeof GuaranteeSchema>;

export const DepositPolicySchema = z.object({
  deadline: DeadlineSchema.optional(),
  amountPercent: AmountPercentSchema.optional(),
  /** The supplier marked this deposit as a portion of the total, the balance due later (Travelport `remainderInd`). */
  partial: z.boolean().optional(),
});

export type DepositPolicy = z.infer<typeof DepositPolicySchema>;

/**
 * Per-rate extra (crib, rollaway, extra adult, breakfast supplement, etc.).
 * Populated from Sabre's PriceCheck response. Codes are OTA standard
 * (26=crib, 91=rollaway, 196=extra adult).
 *
 * `id`, `name`, `scope`, `max` are checkout-form metadata; `id` is the stable
 * key used by `SelectedRoomExtra.extraId`. `scope` and `max` are best-effort
 * — Sabre doesn't return them directly and they may be undefined for many
 * extras, in which case the form falls back to per-stay pricing.
 */
export const RoomExtraSchema = z.object({
  id: z.string().optional(),
  sabreRoomExtraCode: z.number().optional(),
  name: z.string().optional(),
  description: z.string().optional(),
  amount: z.number().optional(),
  currency: z.string().optional(),
  scope: z.enum(['per_stay', 'per_night', 'per_person']).optional(),
  max: z.number().int().positive().optional(),
});

export type RoomExtra = z.infer<typeof RoomExtraSchema>;

export const GuaranteePolicySchema = z.object({
  sabreGuaranteeType: z.string().optional(),
  guaranteesAccepted: z.array(GuaranteeSchema).optional(),
});

export type GuaranteePolicy = z.infer<typeof GuaranteePolicySchema>;

export const MealsSchema = z.object({
  breakfastIncluded: z.boolean().optional(),
  lunchIncluded: z.boolean().optional(),
  dinnerIncluded: z.boolean().optional(),
  mealPlanDescription: z.string().optional(),
});

export type Meals = z.infer<typeof MealsSchema>;

/**
 * Room rate
 */
export const RoomRatesSchema = z.object({
  productCode: z.string(),
  rateKey: z.string(),
  code: z.string().optional(),
  name: z.string().optional(),
  displayName: z.string().optional(),
  description: z.string().optional(),
  roomDescription: z.string().optional(),
  localPricing: RoomRatePricingSchema,
  convertedPricing: RoomRatePricingSchema.optional(),
  refundable: z.boolean().optional(),
  cancellationPolicies: z.array(CancellationPolicySchema).optional(),
  notes: z.string().optional(),
  prePaymentRequired: z.boolean().optional(),
  guaranteeRequired: z.boolean().optional(),
  guaranteePolicy: GuaranteePolicySchema.optional(),
  depositRequired: z.boolean().optional(),
  depositPolicies: z.array(DepositPolicySchema).optional(),
  loyaltyMemberRate: z.boolean().optional(),
  /**
   * Who may book this rate, when not everyone may — "Military and veterans — qualifying ID required
   * at check-in". Absent means anyone. Set from the supplier's structured flags (see
   * `travelport-rate-eligibility.ts`), so a card can badge the rate and never lead with its price.
   */
  eligibility: z.string().optional(),
  loyaltyPoints: z.boolean().optional(),
  loyaltyProgramName: z.string().optional(),
  // Sabre returns 0 for a sold-out rate — accept it and pass it through rather than failing the
  // whole RoomOption parse. Downstream already treats it correctly: the checkout gates read
  // `availableQuantity < 1` as insufficientInventory and `validateRoomAvailability` refuses adds.
  // A refused add also writes it (`room-inventory.ts`): the count the hotel stated, or one fewer
  // than it was asked for when it stated none. The next search or recheck replaces it, count or none.
  availableQuantity: z.number().nonnegative().optional(),
  additionalDetails: z.array(AdditionalDetailSchema).optional(),
  extras: z.array(RoomExtraSchema).optional(),
  promotions: z.array(z.string()).optional(),
  meals: MealsSchema.optional(),

  // Availability & price tracking (populated by RecheckRoomPrice flow)
  available: z.boolean().optional().describe('Whether this specific rate is still available after recheck'),
  priceHistory: z
    .array(
      z.object({
        pricing: RoomRatePricingSchema,
        fetchedAt: zFirestampOptional(),
        trigger: z.enum(['manual', 'auto']).optional().default('manual'),
      }),
    )
    .optional()
    .describe('Historical pricing from rechecks, newest first.'),

  // Occupancy this rate was priced for (Sabre returns rates priced for the requested occupancy).
  // Required on new rates; optional on the schema only so legacy persisted rates can still parse.
  occupancy: OccupancySchema.optional().describe('Occupancy this rate was priced for'),

  // Per-rate timestamp set by the merge step. Used by the rate picker to default to the
  // most recently searched occupancy when nothing else is provided.
  fetchedAt: zFirestampOptional().describe('When this rate was added or last refreshed'),

  /**
   * Set on the rates the room search chose to present, so a client can label and order
   * "Recommended" without inferring it from generated copy (which enrichment overwrites).
   * Replaced or cleared by the next search of the same room; `searchId` says which search.
   */
  recommendation: z
    .object({
      searchId: z.string(),
      rank: z.number().int().min(1),
      tag: z.enum(['lowest', 'flexible', 'best_value', 'best_room', 'suggested']),
      reason: z.string(),
      /**
       * The badge over the room's photo, written for THIS client's trip ("Most space", "Best for kids").
       * Absent for the machine's own picks (the lowest price, the flexible option) and older searches;
       * the card then names the tag.
       */
      label: z.string().optional(),
      /** How many of this room to book — 2 when a family was offered "two of these". */
      rooms: z.number().int().min(1).optional(),
    })
    .optional(),

  // Content generated by room-enrichment agent
  generated: z
    .object({
      name: z.string().optional(),
      description: z.string().optional(),
      /**
       * What the rate includes, one short item each ("Daily breakfast for two", "Airport transfers"),
       * drawn only from the supplier's rate text. Lets the concierge tell two packages at one hotel
       * apart without parsing prose.
       */
      inclusions: z
        .array(z.string())
        .optional()
        .describe(
          'What this rate includes, per the supplier text: e.g. "Daily breakfast for two". Empty means none stated.',
        ),
      /** Restrictions the supplier's text states ("Prepaid, non-refundable", "Minimum three nights"). */
      caveats: z
        .array(z.string())
        .optional()
        .describe(
          'What restricts this rate, per the supplier text: e.g. "Prepaid, non-refundable". Empty means none stated.',
        ),
      cancellationPolicy: z.string().optional(),
      guaranteePolicy: z.string().optional(),
      depositPolicy: z.string().optional(),
      requiresMembership: z.boolean().optional(),
      membershipOrganizationName: z.string().optional(),
      generatedAt: zFirestampOptional().optional(),
    })
    .optional(),

  // Deprecated
  breakfastIncluded: z.boolean().optional(),
  lunchIncluded: z.boolean().optional(),
  dinnerIncluded: z.boolean().optional(),
});

export type RoomRate = z.infer<typeof RoomRatesSchema>;

/**
 * Room selection metadata for itinerary items (type `room`). Shortlist items use
 * `RoomShortlistMetadataSchema` instead: the room, optionally pinned to a rate by the same identity.
 * Identifies a rate by `(productCode, occupancy)` — the stable identity. We
 * deliberately do NOT persist `rateKey` because Sabre rotates rateKeys on every
 * request (search, pricecheck), so any persisted reference would go stale on
 * the next enrichment cycle. Booking flows resolve a fresh rateKey from the
 * room option's current rate at booking time.
 *
 * Each itinerary item is one room; multi-room bookings are modelled as N items.
 */
export const RoomSelectionSchema = z.object({
  productCode: z
    .string()
    .describe(
      'productCode from RoomRatesSchema (the Sabre rate-product identifier). ALWAYS default to the cheapest available rate unless the user has specified otherwise.',
    ),
  occupancy: OccupancySchema.describe('Guests for this individual room'),
});

export type RoomSelection = z.infer<typeof RoomSelectionSchema>;

/**
 * Search context stored with each RoomOption document. Per-rate occupancy lives on
 * each rate, not at the RoomOption level.
 */
export const RoomOptionSearchSchema = z.object({
  hotelId: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  /**
   * The party the search was REQUESTED for, when the caller recorded it. A room can hold rates for
   * several parties: the Travelport tool always also prices the adults alone, as a fallback for a
   * failed child-priced search, so a family search saves two. Without this the rate picker opened on
   * whichever of the two parallel searches resolved a millisecond later, which put a family trip on
   * the adults-only tab — at Sailrock that tab holds one rate, the dearest (production, 2026-09-27).
   */
  occupancy: OccupancySchema.optional(),
});

export type RoomOptionSearch = z.infer<typeof RoomOptionSearchSchema>;

/**
 * One way the hotel says a room may be occupied: caps on the total, the adults and the children —
 * whichever its words gave. "3 Adults Or 2 Adults And 2 Children" is TWO configurations, never one
 * merged "3 adults and 2 children" (a room sold to five). An adults-only or guests-only sentence
 * leaves the other kind whatever headroom is left under its cap, never more.
 */
export const OccupancyConfigurationSchema = z.object({
  total: z.number().int().positive().optional(),
  adults: z.number().int().nonnegative().optional(),
  children: z.number().int().nonnegative().optional(),
});

export type OccupancyConfiguration = z.infer<typeof OccupancyConfigurationSchema>;

/** A capacity the hotel stated in words: the alternatives it named and the sentence they came from, verbatim. */
export const StatedOccupancySchema = z.object({
  configurations: z.array(OccupancyConfigurationSchema).min(1),
  quote: z.string(),
});

export type StatedOccupancy = z.infer<typeof StatedOccupancySchema>;

/** What one supplier call said about a room's capacity: its numeric field, and what its text states. */
export const OccupancySourceSchema = z.object({
  /** The supplier's numeric maximum, verbatim. */
  max: z.number().int().positive().optional(),
  /** The capacity the hotel's own words state, as a model read them. */
  stated: StatedOccupancySchema.optional(),
});

/**
 * Every place a room's capacity was found, kept apart so nothing is flattened before someone
 * needs it: the v12 search (number per Travelport grouping, words in the truncated room text) and
 * v11 Rules (number per booking code, words in the full room text). Rules exists only for rooms a
 * Rules read reached — the curated candidates, the picks, and whatever the hotel's cache holds.
 */
export const RoomOccupancySourcesSchema = z.object({
  search: OccupancySourceSchema.optional(),
  rules: OccupancySourceSchema.optional(),
});

export type RoomOccupancySources = z.infer<typeof RoomOccupancySourcesSchema>;

export const MAX_OCCUPANCY_SOURCES = ['rules', 'rules_text', 'search_text', 'search'] as const;
export const MaxOccupancySourceSchema = z.enum(MAX_OCCUPANCY_SOURCES);
export type MaxOccupancySource = z.infer<typeof MaxOccupancySourceSchema>;

/**
 * Whether the hotel's system priced the children of the party searched. Read from the supplier's
 * availability note on the with-children search; `priced` when it carried none.
 */
export const CHILDREN_PRICING = ['priced', 'not_priced_confirm_at_booking', 'not_priced_ask_hotel'] as const;
export const ChildrenPricingSchema = z.enum(CHILDREN_PRICING);
export type ChildrenPricing = z.infer<typeof ChildrenPricingSchema>;

/**
 * Why a room of a with-children search carries no rate priced for the whole party, as the
 * hotel's system answered — what the rate picker's note says above the adults' rates:
 *   - `no_rates`: the with-children search came back with no rates at all (Accor, IHG and other
 *     providers answer a party with children with an empty property); the room's own stated
 *     capacity did not take everyone either.
 *   - `refused`: the hotel offered other rooms to the party and left this one out (Hilton filters
 *     its with-children answer), or its booking system refused the room when asked about the
 *     party (Marriott, at Rules or at checkout).
 *   - `unchecked`: the hotel's system quoted the room without checking the children (the
 *     identical answer Marriott and the no-support chains return), and the room's own stated
 *     capacity said nothing.
 *   - `too_small`: the room's own capacity, from a source we trust (Rules' number or words, the
 *     search's words), says it does not take the party — whatever else the hotel answered. Two
 *     of the room is the answer, and there is nothing for the team to confirm, so the rate
 *     picker shows no note for it.
 * Absent on a room priced for the party, and on an adults-only search.
 */
export const PARTY_ANSWER = ['no_rates', 'refused', 'unchecked', 'too_small'] as const;
export const PartyAnswerSchema = z.enum(PARTY_ANSWER);
export type PartyAnswer = z.infer<typeof PartyAnswerSchema>;

/** The most people one configuration admits. */
export function configurationTotal(c: OccupancyConfiguration): number {
  return c.total ?? (c.adults ?? 0) + (c.children ?? 0);
}

/**
 * The capacity to show and to decide with: the first source present, in the order the sources
 * earned on 40 production hotels (2026-10-19). Rules' number matched Rules' own words 10 of 10
 * times and the hotel's website 10 of 11; the words are the hotel's own sentence; the v12 words
 * are that sentence truncated; the v12 number belongs to Travelport's grouping, not the room, and
 * was wrong in both directions (it undercounts an adults-only hotel and inherits a bigger sibling's
 * count inside a mixed grouping). Never "the highest": the three cases where the v12 number was
 * higher than Rules were the three mixed groupings, rooms that would have been oversold.
 */
export function chooseMaxOccupancy(
  occupancy: RoomOccupancySources | undefined,
): { max: number; source: MaxOccupancySource } | undefined {
  if (!occupancy) return;
  const rulesWords = occupancy.rules?.stated;
  const rulesWordsMax = rulesWords ? Math.max(...rulesWords.configurations.map(configurationTotal)) : undefined;
  // An adults-only sentence ("/ 3 Adults /") caps the adults, not everyone: a higher number beside
  // it is the total, and the two agree. Words that name a total and differ from the number win —
  // they are the sentence the hotel wrote (the two agreed 10 of 10 times in the survey).
  const wordsAreAdultsOnly = !!rulesWords && rulesWords.configurations.every((c) => c.total === undefined);
  if (
    occupancy.rules?.max &&
    (rulesWordsMax === undefined ||
      rulesWordsMax === occupancy.rules.max ||
      (wordsAreAdultsOnly && rulesWordsMax < occupancy.rules.max))
  ) {
    return { max: occupancy.rules.max, source: 'rules' };
  }
  if (rulesWordsMax !== undefined) return { max: rulesWordsMax, source: 'rules_text' };
  const searchWords = occupancy.search?.stated;
  if (searchWords) {
    return { max: Math.max(...searchWords.configurations.map(configurationTotal)), source: 'search_text' };
  }
  if (occupancy.search?.max) return { max: occupancy.search.max, source: 'search' };
  return;
}

/**
 * Whether a party fits ONE of the stated configurations: adults within the adult cap (or the total
 * when no adult cap is stated), children within the child cap (or what the total leaves after the
 * adults), and everyone within the total. "3 Adults Or 2 Adults And 2 Children" takes 2+2 and 3+0,
 * not 3+1.
 */
export function partyFitsConfigurations(
  party: { adults: number; children: number },
  configurations: ReadonlyArray<OccupancyConfiguration>,
): boolean {
  return configurations.some((c) => {
    const total = configurationTotal(c);
    const adultCap = c.adults ?? total;
    const childCap = c.children ?? Math.max(0, total - party.adults);
    return party.adults <= adultCap && party.children <= childCap && party.adults + party.children <= total;
  });
}

/**
 * RoomOption document stored in Firestore at trips/{tripId}/room-options/{id}.
 * Single source of truth for room shape; combines room content with hotelId, search context, and traceability.
 */
export const RoomOptionSchema = z.object({
  id: z.string().optional(),
  type: z.string().optional(),
  description: z.string(),
  bedTypes: z.array(BedTypesSchema),
  rates: z.array(RoomRatesSchema),
  excludedRates: z.array(RoomRatesSchema).optional(),
  size: z.string().optional(),
  view: z
    .string()
    .optional()
    .describe('Room view as the supplier states it, e.g. "River Views". Absent when the supplier sends none.'),
  /**
   * The capacity to show ("Sleeps N") and to decide with — the winner of `chooseMaxOccupancy` over
   * `occupancy` on a Travelport room (`maxOccupancySource` says which source won); Sabre's own
   * field on a Sabre room. Documents written before 2026-10 carried a number parsed from the
   * room text by a regex, without a source.
   */
  maxOccupancy: z.number().optional(),
  maxOccupancySource: MaxOccupancySourceSchema.optional(),
  /** Every capacity source as found — see `RoomOccupancySourcesSchema`. Travelport only. */
  occupancy: RoomOccupancySourcesSchema.optional(),
  /** Whether the hotel's system priced the children of the party this search asked for. Written on every room of a with-children search. */
  childrenPricing: ChildrenPricingSchema.optional(),
  /** Why no rate here is priced for the whole party, when none is — see `PartyAnswerSchema`. Cleared once the party has a rate on sale again. */
  partyAnswer: PartyAnswerSchema.optional(),
  images: z.array(WidgetImageSchema).optional().default([]),
  amenities: z.array(RoomAmenitiesSchema).optional().default([]),
  accessibleRoom: z.boolean().optional(),
  nonSmokingRoom: z.boolean().optional(),
  hotelId: z.string().describe('ID of the hotel in Firestore'),
  /**
   * Sabre's `RoomID` for this room product, when present. The deterministic doc
   * id includes this, so two rooms that share a generic `type` ("Junior Suite")
   * but represent different products (e.g. Luxury vs Deluxe) don't collapse.
   * Legacy docs created before this field existed leave it unset.
   */
  sabreRoomId: z.string().optional().describe('Sabre RoomID for this room product, when provided'),
  /** Travelport: the hotel's own room type code (the room half of the product code), when the chain has one. */
  roomCode: z.string().optional().describe("The hotel's own room type code, when the supplier exposes one"),
  /** Travelport: the identity key the document id was built from, followed by any earlier keys it was filed under. */
  identityKeys: z.array(z.string()).optional(),
  /** Set on a document a later search replaced under a new identity; the replacement's id. Never deleted, so old references still resolve. */
  supersededBy: z
    .string()
    .optional()
    .describe('Id of the room document that replaced this one after an identity change'),
  search: RoomOptionSearchSchema.describe('Search context (hotelId, dates, room config)'),
  source: z.string().optional().describe('Data source (e.g. sabre)'),
  searchId: z.string().optional().describe('Correlates options from the same search'),

  fetchedAt: zFirestampOptional().describe('When the room data was fetched'),

  // Room-level availability (derived from per-rate availability on recheck)
  available: z.boolean().optional().describe('False when ALL rates are unavailable'),
  partiallyAvailable: z.boolean().optional().describe('True when some (but not all) rates are unavailable'),

  // Content generated by room-enrichment agent
  generated: z
    .object({
      name: z.string().optional(),
      description: z.string().optional(),
      generatedAt: zFirestampOptional().optional(),
    })
    .optional(),
});

export type RoomOption = z.infer<typeof RoomOptionSchema>;

/**
 * Room content only (no hotelId/search). Derived from RoomOptionSchema for API responses,
 * itinerary rooms[], shortlist referenceData, and widget results. id optional for Sabre responses.
 */
export const RoomsSchema = RoomOptionSchema.omit({
  hotelId: true,
  search: true,
  fetchedAt: true,
  source: true,
  searchId: true,
}).extend({ id: z.string().optional() });

export type Room = z.infer<typeof RoomsSchema>;
